import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, AlertTriangle, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, dateFr, apiError } from "@/lib/api";
import Modal from "@/components/Modal";
import PhotoCapture from "@/components/PhotoCapture";

const UNITES_STOCK = ["m", "m²", "m³", "kg", "L", "pièce", "rouleau", "boîte", "paquet", "sac"];

export default function Stocks() {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [q, setQ] = useState("");
  const [stockBas, setStockBas] = useState(false);
  const [tab, setTab] = useState("articles");
  const [addOpen, setAddOpen] = useState(false);
  const [mvtArticle, setMvtArticle] = useState(null);
  const [form, setForm] = useState({ nom: "", reference: "", quantite: "", stock_minimum: "", prix_achat: "", unite: "", unite_autre: "" });
  const [mvt, setMvt] = useState({ type: "entree", quantite: "1", chantier_id: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (stockBas) params.set("stock_bas", "1");
    api.get(`/articles?${params}`).then((r) => setArticles(r.data));
  };

  useEffect(() => { load(); }, [q, stockBas]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "mouvements") api.get("/mouvements").then((r) => setMouvements(r.data));
  }, [tab]);
  useEffect(() => { api.get("/chantiers").then((r) => setChantiers(r.data)); }, []);

  async function createArticle() {
    if (!form.nom.trim()) return toast.error(t("stock.nom_requis"));
    setBusy(true);
    try {
      await api.post("/articles", {
        nom: form.nom, reference: form.reference,
        quantite: Number(form.quantite) || 0,
        stock_minimum: Number(form.stock_minimum) || 0,
        prix_achat: form.prix_achat ? Number(form.prix_achat) : null,
        unite: form.unite === "__autre__" ? form.unite_autre.trim() : form.unite,
      });
      toast.success(t("stock.cree"));
      setAddOpen(false);
      setForm({ nom: "", reference: "", quantite: "", stock_minimum: "", prix_achat: "", unite: "", unite_autre: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function addMouvement() {
    setBusy(true);
    try {
      await api.post(`/articles/${mvtArticle.id}/mouvement`, {
        type: mvt.type, quantite: Number(mvt.quantite) || 0,
        chantier_id: mvt.chantier_id || null,
        note: mvt.type === "entree" ? t("stock.note_reception") : t("stock.note_sortie"),
      });
      toast.success(t("article.mouvement_ok"));
      setMvtArticle(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="page-stocks">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("stock.titre")}</h1>
        <button onClick={() => setAddOpen(true)} className="btn-accent hidden sm:flex" data-testid="btn-ajouter-article-stock">
          <Plus size={20} /> {t("stock.nouveau")}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 bg-white rounded-lg p-1 border border-[#E6DFD1] max-w-sm">
        {[["articles", t("stock.articles")], ["mouvements", t("stock.mouvements")]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-stock-${v}`}
                  className={`h-11 rounded-md font-semibold text-sm ${tab === v ? "bg-[#12100D] text-[#D9A461]" : "text-stone-500"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "articles" ? (
        <>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <input className="input-field !pl-11" placeholder={t("stock.recherche")}
                     value={q} onChange={(e) => setQ(e.target.value)} data-testid="input-recherche-stock" />
            </div>
            <button onClick={() => setStockBas(!stockBas)} data-testid="filter-stock-bas"
                    className={`h-12 px-4 rounded-lg font-semibold text-sm border flex items-center gap-2 shrink-0 ${
                      stockBas ? "bg-[#FBE4E1] text-[#A32E20] border-[#F5C2BC]" : "bg-white text-stone-600 border-[#E6DFD1]"}`}>
              <AlertTriangle size={16} /> {t("stock.filtre_bas")}
            </button>
          </div>

          <div className="panel !p-0 overflow-hidden">
            {articles.length === 0 ? (
              <p className="text-stone-500 text-sm py-10 text-center">
                {stockBas ? t("stock.vide_alerte") : t("stock.vide")}
              </p>
            ) : (
              <div className="divide-y divide-[#E6DFD1]" data-testid="liste-articles">
                {articles.map((a) => {
                  const bas = a.quantite <= a.stock_minimum;
                  return (
                    <div key={a.id} onClick={() => navigate(`/stocks/${a.id}`)}
                         className="flex items-center gap-3 p-4 hover:bg-[#FDFBF7] cursor-pointer"
                         data-testid={`row-article-${a.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{a.nom}</div>
                        <div className="text-xs text-stone-500">
                          {a.reference && <>{t("stock.ref")} {a.reference} · </>}min. {a.stock_minimum} · {t("stock.voir_fiche")}
                        </div>
                      </div>
                      <span className={`border text-xs font-bold px-3 py-1.5 rounded-full shrink-0 ${
                        bas ? "bg-[#FBE4E1] text-[#A32E20] border-[#F5C2BC]" : "bg-[#E4F2E7] text-[#276440] border-[#B8E2C2]"}`}
                            data-testid={`badge-stock-${a.id}`}>
                        {bas ? <AlertTriangle size={12} className="inline mr-1" /> : null}
                        {a.quantite}{a.unite ? ` ${a.unite}` : ""} {t("stock.en_stock")}
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); setMvtArticle(a); setMvt({ type: "entree", quantite: "1", chantier_id: "" }); }}
                              className="btn-secondary !h-11 !px-4 shrink-0" data-testid={`btn-mouvement-${a.id}`}>
                        {t("stock.entree_sortie")}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button onClick={() => setAddOpen(true)} data-testid="fab-ajouter-article"
                  className="sm:hidden fixed bottom-24 right-4 z-30 w-14 h-14 rounded-full bg-[#D9A461] text-[#12100D] flex items-center justify-center border-4 border-[#F7F4EE] active:scale-95">
            <Plus size={26} />
          </button>
        </>
      ) : (
        <div className="panel !p-0 overflow-hidden">
          {mouvements.length === 0 ? (
            <p className="text-stone-500 text-sm py-10 text-center">{t("stock.vide_mouvements")}</p>
          ) : (
            <div className="divide-y divide-[#E6DFD1]" data-testid="liste-mouvements">
              {mouvements.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-4 text-sm">
                  <History size={16} className="text-stone-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{m.article_nom}</div>
                    <div className="text-xs text-stone-500">
                      {dateFr(m.date)}{m.chantier_titre && <> · {m.chantier_titre}</>}{m.note && <> · {m.note}</>}
                    </div>
                  </div>
                  <span className={`font-serif font-bold shrink-0 ${m.type === "entree" ? "text-[#276440]" : "text-[#A32E20]"}`}>
                    {m.type === "entree" ? "+" : "−"}{m.quantite}{m.article_unite ? ` ${m.article_unite}` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={t("stock.modal_titre")} testid="modal-ajout-article">
        <div className="space-y-4">
          <input className="input-field" placeholder={t("stock.nom")} value={form.nom}
                 onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="input-article-nom" />
          <input className="input-field" placeholder={t("stock.reference")} value={form.reference}
                 onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="input-article-reference" />
          <select className="input-field" value={form.unite}
                  onChange={(e) => setForm({ ...form, unite: e.target.value })} data-testid="select-article-unite">
            <option value="">{t("stock.unite_sans")}</option>
            {UNITES_STOCK.map((u) => <option key={u} value={u}>{u}</option>)}
            <option value="__autre__">{t("stock.unite_autre")}</option>
          </select>
          {form.unite === "__autre__" && (
            <input className="input-field" placeholder={t("stock.unite_libre")} value={form.unite_autre}
                   onChange={(e) => setForm({ ...form, unite_autre: e.target.value })} data-testid="input-article-unite-libre" />
          )}
          <div className="grid grid-cols-2 gap-3">
            <input className="input-field" type="number" min="0" placeholder={t("stock.quantite")} value={form.quantite}
                   onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="input-article-quantite" />
            <input className="input-field" type="number" min="0" placeholder={t("stock.seuil")} value={form.stock_minimum}
                   onChange={(e) => setForm({ ...form, stock_minimum: e.target.value })} data-testid="input-article-minimum" />
          </div>
          <input className="input-field" type="number" min="0" step="0.01" placeholder={t("stock.prix_achat")}
                 value={form.prix_achat} onChange={(e) => setForm({ ...form, prix_achat: e.target.value })}
                 data-testid="input-article-prix" />
          <p className="text-xs text-stone-500">{t("stock.note_photo")}</p>
          <button onClick={createArticle} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-article">
            {busy ? <Loader2 className="animate-spin" size={18} /> : t("stock.creer")}
          </button>
        </div>
      </Modal>

      <Modal open={!!mvtArticle} onClose={() => setMvtArticle(null)}
             title={mvtArticle ? t("article.modal_titre", { nom: mvtArticle.nom }) : ""} testid="modal-mouvement">
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[["entree", t("article.entree_long")], ["sortie", t("article.sortie_long")]].map(([v, l]) => (
            <button key={v} onClick={() => setMvt({ ...mvt, type: v })} data-testid={`mvt-type-${v}`}
                    className={`h-12 rounded-lg font-semibold text-sm border ${
                      mvt.type === v ? "bg-[#12100D] text-[#D9A461] border-[#D9A461]/40" : "bg-white border-[#E6DFD1] text-stone-600"}`}>
              {l}
            </button>
          ))}
        </div>
        <label className="block text-sm font-medium mb-1.5">{t("article.quantite")}</label>
        <input className="input-field mb-4" type="number" min="0" value={mvt.quantite}
               onChange={(e) => setMvt({ ...mvt, quantite: e.target.value })} data-testid="input-mvt-quantite" />
        <label className="block text-sm font-medium mb-1.5">{t("article.chantier")}</label>
        <select className="input-field mb-4" value={mvt.chantier_id}
                onChange={(e) => setMvt({ ...mvt, chantier_id: e.target.value })} data-testid="select-mvt-chantier">
          <option value="">{t("commun.aucun")}</option>
          {chantiers.map((c) => (
            <option key={c.id} value={c.id}>{c.titre}</option>
          ))}
        </select>
        {mvtArticle && (
          <PhotoCapture articleId={mvtArticle.id} type="bon_livraison" className="btn-secondary w-full mb-3"
                        testid="btn-photo-bon-livraison">
            {t("article.photo_bl")}
          </PhotoCapture>
        )}
        <button onClick={addMouvement} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-mouvement">
          {busy ? <Loader2 className="animate-spin" size={18} /> : t("article.enregistrer")}
        </button>
      </Modal>
    </div>
  );
}
